// re.rejouis.fr — pop-up d'avis « Que penses-tu de ce nouvel espace de reprise ? ».
// Carte flottante en bas à droite (pleine largeur sur mobile), qui apparaît après
// un temps de présence sur le site — ou peu après la confirmation d'une demande.
// Une seule réponse par navigateur ; fermé sans répondre → on ne redemande pas
// avant 30 jours. Les avis arrivent dans l'onglet « Avis » du dashboard reprise (lab).
import { useEffect, useRef, useState } from 'react';
import { Icons } from './ui.jsx';
import { envoyerAvis } from '../lib/api.js';

const CLE = 'reprise_avis';
const DELAI_MS = 45000;          // présence avant affichage
const DELAI_CONFIRMATION_MS = 3000;
const REDEMANDE_MS = 30 * 86400000;
// Écrans où l'on ne dérange pas (connexion, envoi en cours, caméra, redirection).
const ECRANS_EXCLUS = new Set(['callback', 'soumission', 'scan', 'reprendre']);

function dejaTraite() {
  try {
    const v = JSON.parse(localStorage.getItem(CLE) || 'null');
    if (!v) return false;
    if (v.etat === 'envoye') return true;
    return Date.now() - (v.t || 0) < REDEMANDE_MS;
  } catch { return false; }
}
function memoriser(etat) {
  try { localStorage.setItem(CLE, JSON.stringify({ etat, t: Date.now() })); } catch { /* stockage indisponible */ }
}

export default function AvisPopup({ ecran, barreFixe }) {
  const [delaiEcoule, setDelaiEcoule] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const [note, setNote] = useState(0);
  const [survol, setSurvol] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [etape, setEtape] = useState('saisie'); // saisie | envoi | merci
  const traite = useRef(dejaTraite());

  // Minuterie de présence (une fois par chargement de page).
  useEffect(() => {
    if (traite.current) return undefined;
    const t = setTimeout(() => setDelaiEcoule(true), DELAI_MS);
    return () => clearTimeout(t);
  }, []);

  // Juste après une demande envoyée : moment idéal pour demander un avis.
  useEffect(() => {
    if (traite.current || ecran !== 'confirmation') return undefined;
    const t = setTimeout(() => setDelaiEcoule(true), DELAI_CONFIRMATION_MS);
    return () => clearTimeout(t);
  }, [ecran]);

  useEffect(() => {
    if (delaiEcoule && !traite.current && !ECRANS_EXCLUS.has(ecran)) setOuvert(true);
  }, [delaiEcoule, ecran]);

  async function envoyer() {
    if (!note || etape !== 'saisie') return;
    setEtape('envoi');
    traite.current = true;
    memoriser('envoye');
    try { await envoyerAvis({ note, commentaire: commentaire.trim(), ecran }); } catch { /* best effort */ }
    setEtape('merci');
    setTimeout(() => setOuvert(false), 2500);
  }

  function fermer() {
    // Une note choisie mais pas envoyée compte quand même.
    if (note && etape === 'saisie') { envoyer(); setOuvert(false); return; }
    traite.current = true;
    memoriser(etape === 'merci' ? 'envoye' : 'ferme');
    setOuvert(false);
  }

  if (!ouvert) return null;
  const affichee = survol || note;

  return (
    <div
      role="dialog"
      aria-label="Ton avis sur l'espace de reprise"
      className="avis-popup"
      style={{ bottom: barreFixe ? 104 : 16 }}
    >
      <button onClick={fermer} aria-label="Fermer" className="avis-fermer"><Icons.x size={16} /></button>

      {etape === 'merci' ? (
        <div style={{ textAlign: 'center', padding: '6px 0 2px' }}>
          <div style={{ fontSize: 26 }}>💛</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginTop: 4 }}>Merci pour ton avis !</div>
          <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 2 }}>Il nous aide à améliorer la reprise.</div>
        </div>
      ) : (
        <>
          <div style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.35, paddingRight: 24 }}>
            Que penses-tu de ce nouvel espace de reprise ?
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 10 }} onMouseLeave={() => setSurvol(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setNote(n)}
                onMouseEnter={() => setSurvol(n)}
                aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
                aria-pressed={note === n}
                className="avis-etoile"
                style={{ color: n <= affichee ? 'var(--jaune)' : '#D4D2E8' }}
              >
                <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4l-5.8 3.1 1.1-6.5L2.6 9.4l6.5-.9z" />
                </svg>
              </button>
            ))}
          </div>
          {note ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10, animation: 'proto-pop .25s var(--ease)' }}>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value.slice(0, 1000))}
                placeholder="Un petit mot ? (facultatif)"
                rows={3}
                className="avis-texte"
              />
              <button className="btn btn-primary btn-block" onClick={envoyer} disabled={etape === 'envoi'} style={{ padding: '10px 14px', fontSize: 14 }}>
                {etape === 'envoi' ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
